import { type QueryClient } from '@tanstack/react-query';
import { type NextRouter } from 'next/router';

import '@/shared/features/phone-verification/tracking/events.quiz-trial';

import { trackEvent } from '@zep/logger/tracking';

import { type SuccessBodyTrialOfferClaimRes } from '@/api/gen/_model/successBodyTrialOfferClaimRes';
import {
  claimTrialOffer,
  getGetMyVouchersQueryKey,
} from '@/api/gen/vouchers/vouchers.gen';

import {
  getReturnTo,
  normalizeTrialSource,
} from '../phone-verification/tracking/trialTracking';

import {
  ISSUED_VOUCHER_QUERY_KEY,
  TRIAL_SOURCE_QUERY_KEY,
} from './resolveTrialDestination';

/**
 * 해외 이무체(TWO_WEEKS_FREE) lazy 발급.
 *
 * 국내 핸무체는 휴대폰 인증이 끝나면 서버가 알아서 발급하지만, 해외는 사용자가
 * CTA 를 누른 순간에만 발급된다. 발급 자체는 서버가 멱등 처리하므로 중복 호출로
 * 이용권이 두 장 생기지는 않는다 — 막아야 하는 건 요청이 두 번 나가는 것뿐이다.
 *
 * 발급 뒤 「바로 사용/다음에」 모달은 여기서 열지 않는다. `_app` 의
 * `TrialVoucherRewardModalGate` 가 URL 의 `issuedVoucherId` 신호를 보고 이미 열고
 * 있으므로, 인증 복귀 경로와 같은 신호를 심어 그 게이트를 그대로 쓴다.
 * 모달 경로가 두 벌이 되면 닫기·활성화·토스트까지 두 벌이 된다.
 *
 * 실패는 삼키지 않고 던진다. 자격 상실(`TRIAL_OFFER_NOT_ELIGIBLE`)이면 CTA 자체가
 * 사라져야 하므로 던지기 전에 이용권 조회만 무효화한다 — 안내 문구는 호출부 몫이다.
 */
export const claimTwoWeeksTrial = async ({
  claim = claimTrialOffer,
  queryClient,
  router,
  trialSource,
}: {
  /** 중복 호출 가드가 필요한 호출부는 mutation 의 mutateAsync 를 넘긴다. */
  claim?: () => Promise<SuccessBodyTrialOfferClaimRes>;
  queryClient: QueryClient;
  router: NextRouter;
  trialSource: string;
}) => {
  const normalizedTrialSource = normalizeTrialSource(trialSource);

  try {
    const { data } = await claim();

    trackEvent('quiz_trial_voucher_issue_success', {
      voucher_id: data.voucherHashId,
      trial_source: normalizedTrialSource,
      return_to: getReturnTo(router.asPath),
      voucher_type: 'two_weeks_free',
      trial_region: 'global',
      trial_type: 'two_weeks_free',
    });

    await router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          [ISSUED_VOUCHER_QUERY_KEY]: data.voucherHashId,
          [TRIAL_SOURCE_QUERY_KEY]: normalizedTrialSource,
        },
      },
      undefined,
      { shallow: true },
    );
  } catch (error) {
    await queryClient.invalidateQueries({
      queryKey: getGetMyVouchersQueryKey(),
    });
    throw error;
  }
};
